import { camelCase } from 'change-case'
import { compareDateTimes } from '@/utils/date/validation.js'
import { stripHTML } from '@/utils/html.js'
import { calculateTimeDuration } from '@/utils/time.js'
import type { APIError } from '@/composables/useApi.js'

export const FORM_VALIDATION_DEBOUNCE_MS = 500

export const useFormValidation = (formRef: Ref) => {
  /**
   * Handles form errors by scrolling to the first error and focusing on it.
   * This function is used as a callback for form submission error handling.
   * @param selector - The CSS selector to identify form errors.
   * @param offset - The offset in pixels to account for sticky elements that hide content.
   */
  const handleErrors = (
    selector: string = '.invalid, [aria-invalid="true"], [error]',
    // The default offset is 104px to account for the sticky nord-top-bar (52px) and nord-header (52px).
    offset: number = 104,
    // Use 'instant' in async contexts where smooth scroll may be interrupted by subsequent DOM updates.
    scrollBehavior: ScrollBehavior = 'smooth',
  ) => {
    if (!formRef.value) {
      return
    }

    const formErrors = formRef.value.querySelectorAll(selector)
    const firstError = formErrors[0]

    if (firstError) {
      if (offset) {
        const rectangle = firstError.getBoundingClientRect()
        const elementPosition = rectangle.top + window.scrollY
        window.scrollTo({
          top: elementPosition - offset,
          behavior: scrollBehavior,
        })
      } else {
        firstError.scrollIntoView({
          behavior: scrollBehavior,
        })
      }
      firstError.focus()
    }
  }

  /**
   * Handles server-side validation errors by setting the `nonFieldErrors` field in the errors object.
   * This function is used as a callback for form submission error handling.
   * This way we take advantage of Vee Validate's `errors` object to store these errors.
   * @param error - The API error object containing the validation errors.
   * @param setErrors - The function to set the errors object.
   */
  const collectServerSideErrors = ({
    error,
    setErrors,
  }: {
    error: APIError
    setErrors: (errors: Record<string, string>) => void
  }) => {
    const errorsList = 'result' in error ? error?.result?.error?.errors : []
    const nonFieldSpecificErrors = errorsList?.find(
      (error) => !error.source?.pointer || error.source?.pointer === '/data',
    )

    if (nonFieldSpecificErrors) {
      setErrors({
        nonFieldErrors: nonFieldSpecificErrors.detail ?? '',
      })
    }
  }

  /**
   * Creates a validation function to ensure the end date is on or after the start date.
   * This function is used as the first argument to the refine method in a Zod schema.
   * @param startDateKey - The key for the start date in the Zod schema object.
   * @param  endDateKey - The key for the end date in the Zod schema object.
   * @returns - A validation function that takes a data object and returns true if the validation passes.
   */
  type DateData<T extends string> = Record<T, string>
  const validateEndDateAfterStartDate = <
    StartDateKey extends string,
    EndDateKey extends string,
  >(
    startDateKey: StartDateKey,
    endDateKey: EndDateKey,
  ) => {
    return (data: DateData<StartDateKey | EndDateKey>) => {
      const { [startDateKey]: start_date, [endDateKey]: end_date } = data

      if (!start_date || !end_date) {
        return true
      }
      return compareDateTimes('>=', start_date, end_date)
    }
  }

  /**
   * Creates a validation function to ensure the end time is after the start time on the same or subsequent dates.
   * This function is used as the first argument to the refine method in a Zod schema.
   * @param startDateKey - The key for the start date in the Zod schema object.
   * @param startTimeKey - The key for the start time in the Zod schema object.
   * @param endDateKey - The key for the end date in the Zod schema object.
   * @param endTimeKey - The key for the end time in the Zod schema object.
   * @returns - A validation function that takes a data object and returns true if the validation passes.
   */
  type DateTimeData<T extends string> = Partial<Record<T, string>>
  const validateEndDateTimeAfterStartDateTime = <
    StartDateKey extends string,
    StartTimeKey extends string,
    EndDateKey extends string,
    EndTimeKey extends string,
  >(
    startDateKey: StartDateKey,
    startTimeKey: StartTimeKey,
    endDateKey: EndDateKey,
    endTimeKey: EndTimeKey,
  ) => {
    return (
      data: DateTimeData<StartDateKey | StartTimeKey | EndDateKey | EndTimeKey>,
    ) => {
      const {
        [startDateKey]: start_date,
        [startTimeKey]: start_time,
        [endDateKey]: end_date,
        [endTimeKey]: end_time,
      } = data

      if (
        !start_date ||
        !start_time ||
        !end_date ||
        !end_time ||
        !compareDateTimes('===', start_date, end_date)
      ) {
        return true
      }
      return compareDateTimes('>', start_date, end_date, start_time, end_time)
    }
  }

  type TimeData<T extends string> = Record<T, string>
  const validateEndTimeAfterStartTime = <
    StartTimeKey extends string,
    EndTimeKey extends string,
  >(
    startTimeKey: StartTimeKey,
    endTimeKey: EndTimeKey,
  ) => {
    return (data: TimeData<StartTimeKey | EndTimeKey>) => {
      const { [startTimeKey]: start_time, [endTimeKey]: end_time } = data
      return calculateTimeDuration(start_time, end_time)
    }
  }

  const validateRichTextContentLength = (options: {
    maxLength: number
    debounceMs?: number
  }) => {
    const { maxLength, debounceMs = 200 } = options
    const debouncedStripHTML = useDebounceFn(
      (data: string) => stripHTML(data),
      debounceMs,
    )
    return async (data: string) => {
      const result = await debouncedStripHTML(data)
      return (result?.length ?? 0) <= maxLength
    }
  }

  const regexRules = {
    ISODateTime:
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?(([+-]\d{2}:\d{2})|Z)?$/,
    ISODate: /^\d{4}-\d{2}-\d{2}$/,
  }

  /**
   * Maps backend API error responses to frontend form field names.
   * Automatically converts snake_case to camelCase and handles nested fields.
   *
   * @param error - The API error object containing the validation errors
   * @param setErrors - The function to set the errors object from vee-validate
   * @param specificFieldNameMapper - Optional function to handle special field name mappings
   *
   * @example
   * ```typescript
   * mapServerErrorsToFormFields({
   *   error: apiError,
   *   setErrors,
   *   specificFieldNameMapper: (backendField, segments) => {
   *     if (backendField === 'phone_numbers') return 'phone'
   *     if (['street_address', 'city'].includes(backendField)) return `address.${backendField}`
   *     return null // Let default camelCase conversion handle it
   *   }
   * })
   * ```
   */
  const mapServerErrorsToFormFields = ({
    error,
    setErrors,
    specificFieldNameMapper,
  }: {
    error: APIError
    setErrors: (errors: Record<string, string>) => void
    specificFieldNameMapper?: (
      backendFieldName: string,
      segments: string[],
    ) => string | null
  }): void => {
    if (!('result' in error) || !error.result?.error?.errors) {
      return
    }

    const errors = error.result.error.errors
    const formErrors: Record<string, string> = {}

    errors.forEach((errorItem) => {
      const pointer = errorItem.source?.pointer

      if (!errorItem.detail) {
        return
      }

      if (!pointer || pointer === '/data') {
        formErrors.nonFieldErrors = errorItem.detail
        return
      }

      // Extract the path segments from pointer
      // e.g., "/data/attributes/id_number" or "/data/relationships/phone_numbers"
      const segments = pointer.split('/').filter(Boolean)

      const backendFieldName = segments[segments.length - 1]

      let frontendFieldName = specificFieldNameMapper?.(
        backendFieldName,
        segments,
      )

      if (!frontendFieldName) {
        frontendFieldName = camelCase(backendFieldName)
      }

      formErrors[frontendFieldName] = errorItem.detail
    })

    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors)
    }
  }

  return {
    handleErrors,
    collectServerSideErrors,
    mapServerErrorsToFormFields,
    validateEndDateAfterStartDate,
    validateEndDateTimeAfterStartDateTime,
    validateEndTimeAfterStartTime,
    validateRichTextContentLength,
    regexRules,
  }
}
